import React, { useState, useEffect } from 'react';
import './MigrationProgress.css';

const MigrationProgress = () => {
  const [operatingHoursStatus, setOperatingHoursStatus] = useState(null);
  const [keywordsStatus, setKeywordsStatus] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  // Start both scripts simultaneously
  const startBothScripts = async () => {
    try {
      setError(null);
      setIsRunning(true);
      
      // Start both scripts using the combined endpoint
      const response = await fetch(`${API_BASE_URL}/api/migration/start-both-migrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        console.log('✅ Both scripts started successfully');
        // Start polling for status
        pollStatus();
      } else {
        setError(data.message || 'Failed to start scripts');
        setIsRunning(false);
      }
    } catch (err) {
      setError(err.message);
      setIsRunning(false);
    }
  };

  // Poll status for both scripts
  const pollStatus = async () => {
    const interval = setInterval(async () => {
      try {
        const [ohStatus, kwStatus] = await Promise.all([
          fetch(`${API_BASE_URL}/api/migration/operating-hours-update-status`)
            .then(res => res.json())
            .catch(() => null),
          fetch(`${API_BASE_URL}/api/migration/keywords-generation-status`)
            .then(res => res.json())
            .catch(() => null)
        ]);

        if (ohStatus) setOperatingHoursStatus(ohStatus);
        if (kwStatus) setKeywordsStatus(kwStatus);

        // Check if both are completed
        const ohCompleted = ohStatus?.status === 'completed';
        const kwCompleted = kwStatus?.status === 'completed';

        if (ohCompleted && kwCompleted) {
          clearInterval(interval);
          setIsRunning(false);
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    }, 2000); // Poll every 2 seconds

    // Store interval ID for cleanup
    return () => clearInterval(interval);
  };

  // Auto-start polling if scripts are already running
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const [ohStatus, kwStatus] = await Promise.all([
          fetch(`${API_BASE_URL}/api/migration/operating-hours-update-status`)
            .then(res => res.json())
            .catch(() => null),
          fetch(`${API_BASE_URL}/api/migration/keywords-generation-status`)
            .then(res => res.json())
            .catch(() => null)
        ]);

        if (ohStatus) setOperatingHoursStatus(ohStatus);
        if (kwStatus) setKeywordsStatus(kwStatus);

        const ohInProgress = ohStatus?.status === 'in_progress';
        const kwInProgress = kwStatus?.status === 'in_progress';

        if (ohInProgress || kwInProgress) {
          setIsRunning(true);
          pollStatus();
        }
      } catch (err) {
        console.error('Error checking initial status:', err);
      }
    };

    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatProgress = (progress) => {
    if (!progress) return { percentage: 0, current: 0, total: 0 };
    return {
      percentage: progress.progress || ((progress.current / progress.total) * 100).toFixed(1),
      current: progress.current || 0,
      total: progress.total || 0,
      updated: progress.updated || 0,
      skipped: progress.skipped || 0,
      failed: progress.failed || 0
    };
  };

  const ohProgress = formatProgress(operatingHoursStatus?.progress);
  const kwProgress = formatProgress(keywordsStatus?.progress);

  return (
    <div className="migration-progress-container">
      <div className="migration-progress-header">
        <h1>🔄 Migration Progress Monitor</h1>
        <p>Live progress tracking for Operating Hours and Keywords generation</p>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <div className="migration-controls">
        <button
          onClick={startBothScripts}
          disabled={isRunning}
          className="start-button"
        >
          {isRunning ? '⏳ Running...' : '🚀 Start Both Scripts Simultaneously'}
        </button>
      </div>

      <div className="progress-grid">
        {/* Operating Hours Progress */}
        <div className="progress-card">
          <div className="card-header">
            <h2>🕐 Operating Hours Update</h2>
            <span className={`status-badge ${operatingHoursStatus?.status || 'not_started'}`}>
              {operatingHoursStatus?.status || 'Not Started'}
            </span>
          </div>

          {operatingHoursStatus?.progress && (
            <>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${ohProgress.percentage}%` }}
                  />
                </div>
                <div className="progress-text">
                  {ohProgress.current} / {ohProgress.total} ({ohProgress.percentage}%)
                </div>
              </div>

              <div className="progress-details">
                <div className="detail-item">
                  <span className="label">Current:</span>
                  <span className="value">{operatingHoursStatus.progress.attraction || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Updated:</span>
                  <span className="value success">✅ {ohProgress.updated}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Skipped:</span>
                  <span className="value warning">⏭️ {ohProgress.skipped}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Failed:</span>
                  <span className="value error">❌ {ohProgress.failed}</span>
                </div>
                {operatingHoursStatus.progress.elapsed_seconds && (
                  <div className="detail-item">
                    <span className="label">Elapsed:</span>
                    <span className="value">
                      {Math.floor(operatingHoursStatus.progress.elapsed_seconds / 60)}m{' '}
                      {operatingHoursStatus.progress.elapsed_seconds % 60}s
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {operatingHoursStatus?.status === 'completed' && operatingHoursStatus.results && (
            <div className="completion-summary">
              <h3>✅ Completion Summary</h3>
              <div className="summary-stats">
                <div>Total: {(operatingHoursStatus.results.updated?.length || 0) + (operatingHoursStatus.results.skipped?.length || 0) + (operatingHoursStatus.results.failed?.length || 0)}</div>
                <div>Updated: {operatingHoursStatus.results.updated?.length || 0}</div>
                <div>Skipped: {operatingHoursStatus.results.skipped?.length || 0}</div>
                <div>Failed: {operatingHoursStatus.results.failed?.length || 0}</div>
              </div>
              {operatingHoursStatus.results.error && (
                <div className="error-message" style={{ marginTop: '1rem', padding: '0.75rem', background: '#fee', color: '#c33', borderRadius: '6px' }}>
                  <strong>Error:</strong> {operatingHoursStatus.results.error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Keywords Progress */}
        <div className="progress-card">
          <div className="card-header">
            <h2>🏷️ Keywords Generation</h2>
            <span className={`status-badge ${keywordsStatus?.status || 'not_started'}`}>
              {keywordsStatus?.status || 'Not Started'}
            </span>
          </div>

          {keywordsStatus?.progress && (
            <>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${kwProgress.percentage}%` }}
                  />
                </div>
                <div className="progress-text">
                  {kwProgress.current} / {kwProgress.total} ({kwProgress.percentage}%)
                </div>
              </div>

              <div className="progress-details">
                <div className="detail-item">
                  <span className="label">Current:</span>
                  <span className="value">{keywordsStatus.progress.attraction || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Updated:</span>
                  <span className="value success">✅ {kwProgress.updated}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Skipped:</span>
                  <span className="value warning">⏭️ {kwProgress.skipped}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Failed:</span>
                  <span className="value error">❌ {kwProgress.failed}</span>
                </div>
                {keywordsStatus.progress.elapsed_seconds && (
                  <div className="detail-item">
                    <span className="label">Elapsed:</span>
                    <span className="value">
                      {Math.floor(keywordsStatus.progress.elapsed_seconds / 60)}m{' '}
                      {keywordsStatus.progress.elapsed_seconds % 60}s
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {keywordsStatus?.status === 'completed' && keywordsStatus.results && (
            <div className="completion-summary">
              <h3>✅ Completion Summary</h3>
              <div className="summary-stats">
                <div>Total: {(keywordsStatus.results.updated?.length || 0) + (keywordsStatus.results.skipped?.length || 0) + (keywordsStatus.results.failed?.length || 0)}</div>
                <div>Updated: {keywordsStatus.results.updated?.length || 0}</div>
                <div>Skipped: {keywordsStatus.results.skipped?.length || 0}</div>
                <div>Failed: {keywordsStatus.results.failed?.length || 0}</div>
              </div>
              {keywordsStatus.results.error && (
                <div className="error-message" style={{ marginTop: '1rem', padding: '0.75rem', background: '#fee', color: '#c33', borderRadius: '6px' }}>
                  <strong>Error:</strong> {keywordsStatus.results.error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Overall Status */}
      <div className="overall-status">
        <h3>Overall Status</h3>
        <div className="status-grid">
          <div className="status-item">
            <span className="status-label">Operating Hours:</span>
            <span className={`status-value ${operatingHoursStatus?.status || 'not_started'}`}>
              {operatingHoursStatus?.status === 'completed' ? '✅ Completed' :
               operatingHoursStatus?.status === 'in_progress' ? '⏳ In Progress' :
               '⏸️ Not Started'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Keywords:</span>
            <span className={`status-value ${keywordsStatus?.status || 'not_started'}`}>
              {keywordsStatus?.status === 'completed' ? '✅ Completed' :
               keywordsStatus?.status === 'in_progress' ? '⏳ In Progress' :
               '⏸️ Not Started'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigrationProgress;

